// Normal actions
import { stringify } from 'qs';
import fetchWithDefaults from '../CommonUtils/fetchWithDefaults';
import { urlWithParams } from '../CommonUtils/urlWithParams';
import { downloadFile } from '../CommonUtils/downloadFile';

export const SET_PDF_INFO = 'PDF.SET_PDF_INFO';
export const SET_ERRORS = 'PDF.SET_ERRORS';
export const SET_CLAIMS = 'PDF.SET_CLAIMS';
export const SET_WARNINGS = 'PDF.SET_WARNINGS';
export const SET_LOADING = 'PDF.SET_LOADING';
export const SET_DOWNLOADING = 'PDF.SET_DOWNLOADING';
export const REMOVE_ITEM = 'PDF.REMOVE_ITEM';
export const ADD_ITEM = 'PDF.ADD_ITEM';
export const MOVE_ITEM = 'PDF.MOVE_ITEM';
export const CHANGE_ITEM = 'PDF.CHANGE_ITEM';
export const ADD_TEMPLATE = 'PDF.ADD_TEMPLATE';
export const DELETE_SECTION = 'PDF.DELETE_SECTION';
export const SET_DOWNLOAD_ERRORS = 'PDF.SET_DOWNLOAD_ERRORS';
export const SET_DOC_VAULT_PATH = 'PDF.SET_DOC_VAULT_PATH';

const set_loading = (loading, clientId) => ({
  type: SET_LOADING,
  loading,
  clientId,
});

const set_downloading = (downloading, clientId) => ({
  type: SET_DOWNLOADING,
  downloading,
  clientId,
});

const set_claims = (
  canSaveTemplate,
  canDeleteTemplate,
  canGeneratePdf,
  enhancedReportExperience,
  clientId,
) => ({
  type: SET_CLAIMS,
  canDeleteTemplate,
  canSaveTemplate,
  canGeneratePdf,
  enhancedReportExperience,
  clientId,
});

const set_errors = (errors, clientId) => ({
  type: SET_ERRORS,
  errors,
  clientId,
});

const set_pdf_info = (info, sessionSections, clientId) => ({
  type: SET_PDF_INFO,
  info,
  sessionSections,
  clientId,
});

export const set_warnings = (warnings, clientId) => ({
  type: SET_WARNINGS,
  warnings,
  clientId,
});

export const remove_item = (remove_list, index, clientId) => ({
  type: REMOVE_ITEM,
  remove_list,
  index,
  clientId,
});

export const add_item = (add_list, item, clientId, position) => ({
  type: ADD_ITEM,
  add_list,
  item,
  clientId,
  position,
});

export const move_item = (move_list, drag_index, hover_index, clientId) => ({
  type: MOVE_ITEM,
  move_list,
  drag_index,
  hover_index,
  clientId,
});

export const change_item = (item, list, clientId) => ({
  type: CHANGE_ITEM,
  item,
  list,
  clientId,
});

export const add_template = (name, template, clientId, isFirmLevelTemplate) => ({
  type: ADD_TEMPLATE,
  name,
  template,
  clientId,
  isFirmLevelTemplate,
});

export const delete_section = (index, list, clientId) => ({
  type: DELETE_SECTION,
  index,
  list,
  clientId,
});

export const setDownloadErrors = errors => ({
  type: SET_DOWNLOAD_ERRORS,
  errors,
});

export const setDocVaultPath = path => ({
  type: SET_DOC_VAULT_PATH,
  payload: path,
});

export const save_template = (sections, name, clientId, isFirmLevelTemplate) => {
  const includedButHidden = sections.included.filter(({ hidden }) => hidden);

  return dispatch => {
    ADV.util
      .request({
        uri: 'Adviser/AdvancedPdf/SaveTemplate',
        data: {
          clientId,
          name,
          isFirmLevelTemplate,
          pdfModel: {
            includedSections: sections.included.filter(({ hidden }) => !hidden),
            excludedSections: [...sections.excluded, ...includedButHidden],
          },
        },
        type: 'POST',
        sendStringifyJSON: true,
      })
      .then(resp => {
        if (resp.success) {
          const template = {
            isFirmLevelTemplate,
            includedSections: sections.included,
            excludedSections: sections.excluded,
          };

          dispatch(add_template(name, template, clientId, isFirmLevelTemplate));
          dispatch(get_template(name, clientId, isFirmLevelTemplate));
        }
      });
  };
};

export const delete_template = (name, clientId, isFirmLevelTemplate) => {
  return dispatch => {
    ADV.util
      .request({
        uri: 'Adviser/AdvancedPdf/DeleteTemplate',
        data: {
          name,
          clientId,
          isFirmLevelTemplate,
        },
        type: 'POST',
      })
      .then(resp => {
        if (resp.success) {
          dispatch(fetch_data(clientId));
        } else {
          dispatch(set_errors(resp.errors, clientId));
        }
      })
      .catch(e => dispatch(set_errors([e.message], clientId)));
  };
};

export const get_template = (name, clientId, isFirmLevelTemplate) => {
  return (dispatch, getState) => {
    if (name === 'Default Template') dispatch(fetch_data(clientId));
    else
      ADV.util
        .request({
          uri: 'Adviser/AdvancedPdf/GetTemplate',
          data: {
            name,
            clientId,
            isFirmLevelTemplate,
          },
        })
        .then(resp => {
          if (resp.success) {
            const subdivision = getState().PDF[clientId];
            const sessionSections = subdivision ? subdivision.sections : null;
            dispatch(set_pdf_info(resp.payload.viewData, sessionSections, clientId));
          } else {
            dispatch(set_errors(resp.errors, clientId));
          }
        })
        .catch(e => {
          dispatch(set_errors([e.message], clientId));
        })
        .then(() => {
          dispatch(set_loading(false, clientId));
        });
  };
};

export const fetch_data = clientId => {
  return (dispatch, getState) => {
    dispatch(set_loading(true, clientId));
    ADV.util
      .request({
        uri: 'Adviser/AdvancedPdf',
        data: {
          clientId,
        },
      })
      .then(resp => {
        if (resp.success) {
          const subdivision = getState().PDF[clientId];
          const sessionSections = subdivision ? subdivision.sections : null;
          dispatch(set_pdf_info(resp.payload.viewData, sessionSections, clientId));
          dispatch(
            set_claims(
              resp.payload.viewData.canSaveTemplate,
              resp.payload.viewData.canDeleteTemplate,
              resp.payload.viewData.canGeneratePdf,
              resp.payload.viewData.enhancedReportExperience,
              clientId,
            ),
          );

          dispatch(setDocVaultPath(resp.payload.viewData.savedReportsPath || null));
        } else {
          dispatch(set_errors(resp.errors, clientId));
        }
      })
      .catch(e => {
        dispatch(set_errors([e.message], clientId));
      })
      .then(() => {
        dispatch(set_loading(false, clientId));
      });
  };
};

export const downloadPdf = (sections, clientId, fpId) => async dispatch => {
  dispatch(setDownloadErrors(null));

  const body = stringify({
    pdf: true,
    json: JSON.stringify({
      includedSections: sections.included.filter(({ hidden }) => !hidden),
      excludedSections: sections.excluded.filter(({ hidden }) => !hidden),
    }),
    __RequestVerificationToken: ADV.settings.tokens.xsrf,
  });

  const queryParams = { clientId, fpId };

  if (ADV.data.cx) {
    queryParams.cx = true;
  }

  const response = await fetchWithDefaults(urlWithParams('/Consumer/Print', queryParams), {
    method: 'POST',
    body,
    headers: {
      'content-type': 'application/x-www-form-urlencoded',
      'x-requested-with': 'pdf', // To avoid "Can not convert the output to PDF during an AJAX request" error
    },
    showSpinner: true,
  });

  if (response.ok) {
    const fileInfoHeader = response.headers.get('content-disposition');
    const fileName = decodeURIComponent(fileInfoHeader.split('=')[1]).replace("UTF-8''", '');
    const fileBlob = await response.blob();

    downloadFile(fileName, fileBlob);
  } else {
    const errorMessage =
      'The PDF failed to generate. Please try again, and contact us if you continue to have this issue.';

    dispatch(setDownloadErrors([errorMessage]));
  }
};
